import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Statuses where deletion is blocked server-side too (PO exists / released / completed).
const LOCKED_STATUSES = new Set(['po_created', 'po_released', 'completed']);

const RequisitionDeleteButton = ({ requisition, onDeleted, size = 'sm' }) => {
  const { user, getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Client-side visibility guard mirrors backend rules for a clean UX.
  if (!user || user.role === 'finance') return null;
  if (LOCKED_STATUSES.has(requisition?.status)) return null;
  if (user.role === 'employee') {
    const isOwner = requisition?.employee_id === user.id;
    const isCoRequester = (requisition?.co_requesters || []).includes(user.id);
    if (!(isOwner || isCoRequester)) return null;
  }

  const handleDelete = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/requisitions/${requisition.id}`, { headers: getAuthHeader() });
      toast.success(`Deleted requisition for ${requisition.reagent_name}`);
      setOpen(false);
      if (onDeleted) onDeleted(requisition);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete requisition');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant="ghost"
        className="text-red-600 hover:text-red-700 hover:bg-red-50"
        onClick={() => setOpen(true)}
        data-testid={`delete-requisition-button-${requisition.id}`}
        title={`Delete requisition for ${requisition.reagent_name}`}
      >
        <Trash size={16} weight="duotone" />
      </Button>

      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-requisition-dialog">
          <DialogHeader>
            <DialogTitle>Delete requisition?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-text-secondary">
            <p>
              You&apos;re about to delete the requisition for{' '}
              <span className="font-semibold text-text-primary">{requisition.reagent_name}</span>{' '}
              (<span className="font-mono">{requisition.catalogue_number}</span> · {requisition.vendor_name}).
            </p>
            <p>
              Any quotations attached to this requisition will also be removed. This does not affect
              other requisitions in the same vendor batch.
            </p>
            <p className="text-red-600 font-medium">This action cannot be undone.</p>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
              data-testid="cancel-delete-requisition-button"
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={busy}
              data-testid="confirm-delete-requisition-button"
            >
              <Trash size={16} className="mr-2" weight="duotone" /> {busy ? 'Deleting…' : 'Delete requisition'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RequisitionDeleteButton;
